import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MdStorefront, MdPause, MdPlayArrow, MdLocalFireDepartment,
  MdDoNotDisturbOn, MdCheckCircle, MdExpandMore,
} from "react-icons/md";
import { useAuth } from "contexts/AuthContext";
import { getRestaurant } from "services/authService";
import {
  pauseOrders, resumeOrders, setBusy as apiSetBusy, clearBusy,
  openNow, closeNow, followSchedule,
} from "services/operationsService";

function fmtTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

// Small dropdown of preset durations.
function DurationMenu({ label, icon, tone, options, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-50 ${tone}`}
      >
        {icon}
        {label}
        <MdExpandMore className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-44 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg dark:border-navy-700 dark:bg-navy-800">
          {options.map((opt) => (
            <button
              key={opt.label}
              onClick={() => { setOpen(false); onPick(opt.minutes, opt.extra); }}
              className="block w-full px-4 py-2.5 text-left text-xs font-medium text-navy-700 hover:bg-gray-50 dark:text-white dark:hover:bg-navy-700"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OperationalStatusBar() {
  const { restaurantId } = useAuth();
  const [st, setSt] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const data = await getRestaurant(restaurantId);
      setSt(data.data || data);
    } catch {}
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Merge the operational payload the endpoints return back into local state.
  const apply = async (fn) => {
    setSaving(true);
    try {
      const res = await fn();
      const next = res?.data || res;
      if (next) setSt((prev) => ({ ...prev, ...next }));
    } catch {} finally {
      setSaving(false);
    }
  };

  if (!st) return null;

  const accepting = st.accepting_orders !== false && st.accepting_orders !== 0;
  const isBusy    = !!st.is_busy;
  const isOpen    = st.is_open !== false && st.is_open !== 0;
  const override  = st.open_override; // 'open' | 'closed' | null (effective, from endpoint)
  const resumeAt  = fmtTime(st.accepting_orders_until);
  const busyAt    = fmtTime(st.busy_until);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl bg-white p-3 shadow-sm dark:bg-navy-800">
      <span className="mr-1 flex items-center gap-1.5 text-sm font-bold text-navy-700 dark:text-white">
        <MdStorefront className="h-5 w-5 text-brand-500" />
        Service
      </span>

      {/* Accepting orders */}
      {accepting ? (
        <DurationMenu
          label="Pause commandes"
          icon={<MdPause className="h-4 w-4" />}
          tone="border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-navy-600 dark:bg-navy-700 dark:text-amber-400"
          disabled={saving}
          options={[
            { label: "15 minutes", minutes: 15 },
            { label: "30 minutes", minutes: 30 },
            { label: "1 heure", minutes: 60 },
            { label: "Jusqu'à reprise manuelle", minutes: 0 },
          ]}
          onPick={(minutes) => apply(() => pauseOrders(restaurantId, minutes))}
        />
      ) : (
        <button
          disabled={saving}
          onClick={() => apply(() => resumeOrders(restaurantId))}
          className="flex items-center gap-1.5 rounded-xl bg-amber-500 px-3 py-2 text-xs font-bold text-white hover:bg-amber-600 disabled:opacity-50"
        >
          <MdPlayArrow className="h-4 w-4" />
          En pause{resumeAt ? ` (reprend ${resumeAt})` : ""} — Reprendre
        </button>
      )}

      {/* Busy mode */}
      {isBusy ? (
        <button
          disabled={saving}
          onClick={() => apply(() => clearBusy(restaurantId))}
          className="flex items-center gap-1.5 rounded-xl bg-orange-500 px-3 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50"
        >
          <MdLocalFireDepartment className="h-4 w-4" />
          Occupé +{st.extra_prep_minutes || 0} min{busyAt ? ` (→ ${busyAt})` : ""} — Terminer
        </button>
      ) : (
        <DurationMenu
          label="Mode occupé"
          icon={<MdLocalFireDepartment className="h-4 w-4" />}
          tone="border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-navy-600 dark:bg-navy-700 dark:text-orange-400"
          disabled={saving}
          options={[
            { label: "+10 min (1h)", minutes: 60, extra: 10 },
            { label: "+15 min (1h)", minutes: 60, extra: 15 },
            { label: "+20 min (1h)", minutes: 60, extra: 20 },
            { label: "+30 min (2h)", minutes: 120, extra: 30 },
          ]}
          onPick={(minutes, extra) => apply(() => apiSetBusy(restaurantId, minutes, extra))}
        />
      )}

      {/* Emergency open / close */}
      {override === "closed" ? (
        <button
          disabled={saving}
          onClick={() => apply(() => followSchedule(restaurantId))}
          className="flex items-center gap-1.5 rounded-xl bg-red-500 px-3 py-2 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-50"
        >
          <MdDoNotDisturbOn className="h-4 w-4" />
          Fermé manuellement — Rouvrir
        </button>
      ) : isOpen ? (
        <DurationMenu
          label="Fermer maintenant"
          icon={<MdDoNotDisturbOn className="h-4 w-4" />}
          tone="border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 dark:border-navy-600 dark:bg-navy-700 dark:text-red-400"
          disabled={saving}
          options={[
            { label: "30 minutes", minutes: 30 },
            { label: "1 heure", minutes: 60 },
            { label: "2 heures", minutes: 120 },
            { label: "Jusqu'à réouverture manuelle", minutes: 0 },
          ]}
          onPick={(minutes) => apply(() => closeNow(restaurantId, minutes))}
        />
      ) : (
        <DurationMenu
          label="Ouvrir maintenant"
          icon={<MdCheckCircle className="h-4 w-4" />}
          tone="border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-navy-600 dark:bg-navy-700 dark:text-green-400"
          disabled={saving}
          options={[
            { label: "1 heure", minutes: 60 },
            { label: "2 heures", minutes: 120 },
            { label: "Jusqu'à fermeture manuelle", minutes: 0 },
          ]}
          onPick={(minutes) => apply(() => openNow(restaurantId, minutes))}
        />
      )}

      {override === "open" && (
        <button
          disabled={saving}
          onClick={() => apply(() => followSchedule(restaurantId))}
          className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300"
        >
          Suivre l'horaire
        </button>
      )}
    </div>
  );
}
